import { getCurrentInstance, onBeforeUnmount } from 'vue';
import langPtBr from 'quasar/lang/pt-BR';
import { Notify, Dialog, Loading, AppFullscreen, Meta, QTooltip } from 'quasar';

// Quasar's own stylesheet. @quasar/vite-plugin does not inject it: it only compiles
// this import with src/quasar-variables.sass (the `sassVariables` option). Without it
// the bundle has no .q-btn, .q-dialog, .row, .q-pa-md... It comes right before
// quasar.styl so the app's global rules still override it, the order Quasar 1 had with
// `@import '~quasar-styl'` at the top of that file.
import 'quasar/src/css/index.sass';
import '@/styles/quasar.styl';
import '@quasar/extras/roboto-font/roboto-font.css';
import '@quasar/extras/material-icons/material-icons.css';

/**
 * Quasar 2 options, consumed by `app.use(Quasar, options)` in main.js.
 *
 * What is gone compared to Quasar 1:
 *
 * - The hand-maintained `components: { ... }` list of ~90 components and the
 *   `directives: { Ripple, ClosePopup }` block. @quasar/vite-plugin auto-imports
 *   components and directives by their use in templates. A `q-*` missing from the old
 *   list surfaced as "Unknown custom element" at runtime; now there is no list to miss.
 * - `quasar/dist/quasar.ie.polyfills`, which does not exist in Quasar 2 (IE support
 *   ended with Vue 2).
 *
 * The language file was also renamed: `quasar/lang/pt-br` is now `pt-BR`.
 */
export default {
  lang: langPtBr,
  plugins: { Meta, Notify, Dialog, Loading, AppFullscreen },
};

/**
 * Dialogs created by the Dialog plugin (`Dialog.create`, `this.$q.dialog`) stay open when
 * the route changes, as they did under Quasar 1.
 *
 * Quasar 2 mounts plugin dialogs with the app's context, router included, and QDialog
 * hides a non-persistent dialog on every route change. Quasar 1 looked for the app under
 * `#q-app` to parent them; this app mounts on `#app`, so its plugin dialogs had no router
 * and never closed on navigation. Code relies on that: postFecharCaixa shows "Caixa
 * fechado!" and then navigates to /caixas, which now dismissed the message at once.
 * `<q-dialog>` in templates had the router under Quasar 1 as well and is left alone.
 *
 * Must run after app.use(Quasar): the plugin's install assigns `create` and `$q.dialog`.
 */
export function manterDialogosNaTrocaDeRota(app) {
  const criar = Dialog.create;
  const criarSemFecharNaRota = opcoes => criar({ noRouteDismiss: true, ...opcoes });
  Dialog.create = criarSemFecharNaRota;
  app.config.globalProperties.$q.dialog = criarSemFecharNaRota;
}

/**
 * Keeps a QTooltip from blocking every programmatic focus in the app (Quasar 2.33.2).
 *
 * Quasar 2 queues component focus calls (QDialog autofocus, `focus()` on a QInput or
 * QSelect) while a popup opens: the popup raises a wait flag on show and drops it when
 * its transition ends. QTooltip runs that transition end on the same timer slot as its
 * show/hide delays, so a delayed show or hide that turns out to be a no-op cancels it
 * and the flag stays up. Clicking a button within ~300ms of the pointer reaching it,
 * when the button opens a dialog, does exactly that: the dialog takes the focus
 * (focusout starts the hide and cancels the show's end), then its backdrop covers the
 * button (pointerleave cancels the hide's end). Every focus() after that waits in the
 * queue until the pointer goes over that button again: in the caixa's purchase dialog
 * Enter stops jumping to the next field. Quasar 1 had no such queue.
 *
 * When a hide does not reach its end, the fix runs show() and hide() in one tick. Nothing
 * renders in between, and the hide registers a new end on the free slot, which drops
 * the flag. The wait adds the tooltip's show delay, so a pointer that comes back right
 * after the hide still gets its show (no tooltip in the app sets a delay). Left alone on
 * purpose:
 * - a show that misses its end holds the flag only while the tooltip is visible (its
 *   hide ends normally and drops it), and repairing it would unregister the ESC key;
 * - tooltips with v-model (none in the app): the hide() that cancels the end emits
 *   update:modelValue, and any event after beforeHide clears the check.
 *
 * The end events have to reach the wrapper by both ways QTooltip emits: useModelToggle
 * reads the instance's `emit`, QTooltip's own `show`/`hide` use the setup context's,
 * which Vue's production build copies before setup runs (dev reads it on every call).
 * tests/unit/__tests__/infra/tooltip-trava-foco.spec.js runs in both builds and
 * reproduces the lock; recheck it when upgrading Quasar.
 */
const tooltipsDestravados = new WeakSet();

export function destravarFocoDosTooltips(componente = QTooltip) {
  if (tooltipsDestravados.has(componente)) return;
  tooltipsDestravados.add(componente);

  const setupOriginal = componente.setup;
  componente.setup = function (props, contexto) {
    const vm = getCurrentInstance();
    const emitir = vm.emit;
    let conferencia = null;

    // any event after beforeHide means the transition moved on
    const emit = (evento, ...args) => {
      clearTimeout(conferencia);
      if (evento === 'beforeHide') {
        conferencia = setTimeout(() => {
          vm.proxy.show();
          vm.proxy.hide();
        }, Number(props.transitionDuration) + Number(props.delay) + 100);
      }
      return emitir(evento, ...args);
    };

    vm.emit = emit;
    onBeforeUnmount(() => clearTimeout(conferencia));

    return setupOriginal(props, { attrs: contexto.attrs, slots: contexto.slots, emit, expose: contexto.expose });
  };
}
